import React, { useState, useRef } from 'react';
import { Box, Tooltip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const MONO = "'IBM Plex Mono', monospace";

// Fits the thumbnail within this box (both dimensions bounded, aspect ratio
// preserved) regardless of whether the source drawing is landscape or portrait.
const THUMB_MAX_WIDTH = 220;
const THUMB_MAX_HEIGHT = 170;

// Navigation minimap + zoom slider for the drawing canvas (DrawingView.js).
// Pan is native container scroll there, with no pan *state* — this component
// reads the parent's `viewportRect` (throttled scroll geometry) to draw the
// "you are here" rectangle, and drives navigation entirely through handlers
// the parent already owns (`onJumpTo` = scrollToBBox, `containerRef` for
// direct scroll during a drag, `onZoomSlider` = the deferred-scroll zoom
// pattern already used by zoomToBBox/zoomToPoint) rather than inventing any
// new pan/zoom mechanism of its own.
const DrawingMinimap = ({
  imageBlobUrl,
  imageSize,        // [width, height] — full drawing, native pixels
  containerRef,      // the scrollable canvas container (for direct scroll during drag)
  scale,             // current zoom, for the % label
  sliderValue,       // 0..1, precomputed by the parent (log-mapped scale)
  viewportRect,      // { scrollLeft, scrollTop, clientWidth, clientHeight, scrollWidth, scrollHeight }
  onJumpTo,           // (bbox) => void — click-to-center, image-space
  onZoomSlider,       // (t: 0..1) => void
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const thumbRef = useRef(null);
  const draggingRef = useRef(false);

  if (!imageBlobUrl || !imageSize || !imageSize[0] || !imageSize[1]) return null;

  const thumbScale = Math.min(THUMB_MAX_WIDTH / imageSize[0], THUMB_MAX_HEIGHT / imageSize[1]);
  const thumbWidth = imageSize[0] * thumbScale;
  const thumbHeight = imageSize[1] * thumbScale;

  const hasScroll = viewportRect && viewportRect.scrollWidth > 0 && viewportRect.scrollHeight > 0;
  const rect = hasScroll ? {
    left: (viewportRect.scrollLeft / viewportRect.scrollWidth) * thumbWidth,
    top: (viewportRect.scrollTop / viewportRect.scrollHeight) * thumbHeight,
    width: Math.min(thumbWidth, (viewportRect.clientWidth / viewportRect.scrollWidth) * thumbWidth),
    height: Math.min(thumbHeight, (viewportRect.clientHeight / viewportRect.scrollHeight) * thumbHeight),
  } : null;

  // Fraction across the thumbnail (0..1, clamped) for a pointer event.
  const thumbFraction = (e) => {
    const box = thumbRef.current.getBoundingClientRect();
    return {
      fx: Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)),
      fy: Math.max(0, Math.min(1, (e.clientY - box.top) / box.height)),
    };
  };

  const handleThumbClick = (e) => {
    if (draggingRef.current) return; // suppress the click that ends a drag
    const { fx, fy } = thumbFraction(e);
    onJumpTo({ x: fx * imageSize[0], y: fy * imageSize[1], width: 0, height: 0 });
  };

  // Dragging the viewport rectangle scrolls the real container directly and
  // instantly (no smooth-scroll) — smooth-scroll animates toward a target and
  // queues, which fights continuous mouse movement instead of tracking it.
  const handleRectMouseDown = (e) => {
    e.stopPropagation();
    draggingRef.current = true;
    const move = (moveEvent) => {
      const container = containerRef.current;
      if (!container || !hasScroll) return;
      const { fx, fy } = thumbFraction(moveEvent);
      const left = Math.max(0, Math.min(viewportRect.scrollWidth - viewportRect.clientWidth, fx * viewportRect.scrollWidth - viewportRect.clientWidth / 2));
      const top = Math.max(0, Math.min(viewportRect.scrollHeight - viewportRect.clientHeight, fy * viewportRect.scrollHeight - viewportRect.clientHeight / 2));
      container.scrollTo({ left, top });
    };
    const up = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (collapsed) {
    return (
      <Tooltip title="Show navigator" placement="left">
        <Box component="button" onClick={() => setCollapsed(false)}
          sx={{ position: 'absolute', right: '14px', bottom: '14px', zIndex: 5, width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,26,51,.92)', border: '1px solid rgba(120,150,200,.3)', borderRadius: '8px', color: '#9fb2d4', cursor: 'pointer', backdropFilter: 'blur(4px)', '&:hover': { color: '#eaf0fb', borderColor: 'rgba(120,150,200,.5)' } }}>
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ position: 'absolute', right: '14px', bottom: '14px', zIndex: 5, width: `${thumbWidth + 20}px`, background: 'rgba(13,26,51,.92)', border: '1px solid rgba(120,150,200,.3)', borderRadius: '10px', p: '10px', backdropFilter: 'blur(4px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
        <Box sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.08em', textTransform: 'uppercase', color: '#7f93b8' }}>Navigator</Box>
        <Box component="button" onClick={() => setCollapsed(true)} title="Hide navigator"
          sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', display: 'flex', p: '2px', '&:hover': { color: '#eaf0fb' } }}>
          <ChevronRightIcon sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Box
        ref={thumbRef}
        onClick={handleThumbClick}
        sx={{ position: 'relative', width: `${thumbWidth}px`, height: `${thumbHeight}px`, borderRadius: '4px', overflow: 'hidden', cursor: 'pointer', border: '1px solid rgba(120,150,200,.24)' }}
      >
        <Box component="img" src={imageBlobUrl} alt="" draggable={false}
          sx={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block', userSelect: 'none', pointerEvents: 'none' }} />
        {rect && (
          <Box
            onMouseDown={handleRectMouseDown}
            sx={{
              position: 'absolute', left: `${rect.left}px`, top: `${rect.top}px`,
              width: `${rect.width}px`, height: `${rect.height}px`,
              border: '2px solid #f4716a', background: 'rgba(244,113,106,.12)',
              boxSizing: 'border-box', cursor: 'grab', '&:active': { cursor: 'grabbing' },
            }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '10px' }}>
        <Box component="input"
          type="range" min={0} max={1} step={0.001}
          value={Number.isFinite(sliderValue) ? sliderValue : 0}
          onChange={(e) => onZoomSlider(parseFloat(e.target.value))}
          sx={{ flex: 1, accentColor: '#2dd4bf', cursor: 'pointer' }}
        />
        <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#cdd9ee', minWidth: '38px', textAlign: 'right', userSelect: 'none' }}>
          {Math.round(scale * 100)}%
        </Box>
      </Box>
    </Box>
  );
};

export default DrawingMinimap;
